import { useCallback, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import { SUPPORT_EMAIL } from '@/components/fatality/const';
import { getConfig } from '@/config/config.ts';
import { getUserEmail } from '@/sgwt/sgwtConnect.ts';
import { useAppDispatch } from '@/store/hooks.ts';
import type { IntlMessageId } from '@/types/intl.js';

type Props = {
  reason: 'userInfoFetchFailed' | 'noClient' | 'noProduct';
};

export const UnsatisfiedPrerequisitesModal = ({ reason }: Props) => {
  const dispatch = useAppDispatch();
  const [userEmail, setUserEmail] = useState(getUserEmail() ?? '');

  const handleEmailChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setUserEmail(event.target.value);
  }, []);

  const [helpRequestStatus, setHelpRequestStatus] = useState<'unsent' | 'sending' | 'succeed' | 'failed'>('unsent');

  const titleI18nId: IntlMessageId =
    `fatal.unsatisfiedPrerequisitesModal.title.${reason === 'userInfoFetchFailed' ? 'userInfoFetchFailed' : 'configProblem'}` as const;

  const sendHelpRequest = useCallback(() => {
    setHelpRequestStatus('sending');
    const details = `${reason === 'userInfoFetchFailed' ? 'Could not retrieve user informations.' : reason === 'noClient' ? 'User has no client account configured.' : 'User has no products configured.'}\n\nUser email: ${userEmail}`;

    dispatch(
      sendHelpRequestThunk({
        config: 'access',
        details,
        templateId: getConfig().app.mailTemplateIds.templateIdForAccess,
        templateIdConfirmation: getConfig().app.mailTemplateIds.templateIdForAccessConfirmation,
      }),
    ).then(({ payload }) => {
      setHelpRequestStatus(payload === 'succeed' ? 'succeed' : 'failed');
    });
  }, [reason, userEmail]);

  return (
    <>
      <div className="h-100" /> {/* having page layout ok even with no content */}
      <Modal show backdrop="static">
        <Modal.Header>
          <FormattedMessage tagName="h4" id={titleI18nId} />
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-16px" data-e2e="unsatisfied-prerequisites-modal">
          <FormattedMessage tagName="p" id={`fatal.unsatisfiedPrerequisitesModal.reason.${reason}`} />
          <FormattedMessage
            tagName="p"
            id="fatal.unsatisfiedPrerequisitesModal.assistance"
            values={{ mailLink: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> }}
          />
          <input type="text" className="form-control input-lg w-100" value={userEmail} onChange={handleEmailChange} />
        </Modal.Body>
        <Modal.Footer>
          {helpRequestStatus === 'succeed' && (
            <div className="text-success">
              <FormattedMessage id="fatal.helpRequestSendingSucceeded" />
            </div>
          )}
          {helpRequestStatus === 'failed' && (
            <div className="text-danger">
              <FormattedMessage
                id="fatal.helpRequestSendingFailed"
                values={{ mailLink: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> }}
              />
            </div>
          )}
          {(helpRequestStatus === 'unsent' || helpRequestStatus === 'sending') && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={sendHelpRequest}
              disabled={helpRequestStatus === 'sending'}
            >
              {helpRequestStatus === 'sending' && <div className="me-2 spinner spinner-sm" />}
              <FormattedMessage id="fatal.unsatisfiedPrerequisitesModal.submit" />
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};
